import { useEffect, useMemo, useState } from "react";
import { Box, FormControl, FormLabel, Select, Spinner, Text } from "@chakra-ui/react";
import { CustomToast } from "../../components";
import { useFetch } from "../../hooks/useFetch";
import { cardProps, labelProps } from "./billingui";
import ProjectBillingTab from "./projectbillingtab";


interface Props {
  customerId: number;
  fallbackProjects?: any[];
  companyId?: number | null;
  pocId?: number | null;
}

export default function CustomerProjectBilling({ customerId, fallbackProjects, companyId, pocId }: Props) {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectId, setProjectId] = useState<number | "">("");
  // const [listKey, setListKey] = useState(0);

  // same call CustomerProjectList uses, so both show the same projects
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const q = new URLSearchParams();
        if (customerId) q.append("customerId", String(customerId));
        if (companyId !== undefined && companyId !== null) q.append("companyId", String(companyId));
        if (pocId !== undefined && pocId !== null) q.append("pocId", String(pocId));

        const res = await fetchApi<any>(`Customers/ProjectDetails?${q.toString()}`, "GET", null, null, "");
        const l = Array.isArray(res) ? res : res?.items || res?.data || res?.projects || [];
        setList(l.length ? l : (fallbackProjects ?? []));
      } catch {
        setList(fallbackProjects ?? []);
      } finally {
        setLoading(false);
      }
    };
    if (customerId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, companyId, pocId]);

  const options = useMemo(
    () =>
      list
        .map((p) => ({
          id: Number(p.projectId || p.id),
          name: p.projectName ?? p.name ?? `Project #${p.projectId || p.id}`,
        }))
        .filter((p) => p.id),
    [list],
  );

  useEffect(() => {
    if (options.length === 1) setProjectId(options[0].id);
  }, [options]);

  if (loading) return <Spinner />;

  return (
    <Box>
      <Box {...cardProps} p={4} mb={4}>
        <FormControl maxW="360px">
          <FormLabel {...labelProps}>
            Project
            <Text as="span" color="red.500">
              {" "}
              *
            </Text>
          </FormLabel>
          <Select size="sm" borderRadius="md" placeholder="Select project" value={projectId} onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : "")}>
            {options.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </FormControl>
        {options.length === 0 && (
          <Text fontSize="xs" color="gray.500" mt={2}>
            This customer has no projects yet.
          </Text>
        )}
      </Box>

      {projectId ? (
        <ProjectBillingTab key={projectId} projectId={projectId} />
      ) : (
        options.length > 0 && (
          <Box {...cardProps} p={8} textAlign="center">
            <Text color="gray.500" fontSize="sm">
              Select a project to view or create its billing plan.
            </Text>
          </Box>
        )
      )}
 
    </Box>
  );
}
